import { styled } from "@mui/material";
import { rem } from "polished";
import React, { useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { DdppLegalBanner, Footer, footerHeight } from "src/components";
import {
  ariaLabel,
  DDPP_BANNER_VARIANT_BY_AGENCY_ID,
  SITE_MAP_PAGE,
} from "src/constants";
import {
  type AccountFragment,
  type CustomerFragment,
} from "src/graphql/generated/types";
import { useDeviceWidth, useHasFeatureFlagActivated } from "src/hooks";
import { type DeviceProps } from "src/types/device-props";
import { isTrusteeCouncilMember } from "src/utils";

import { FeatureFlags } from "@emeria/feature-flags";

import { MainLayoutAccountInfosDesktop } from "./AccountInfosDesktop";
import { MainLayoutAccountInfosMobile } from "./AccountInfosMobile";
import { MainLayoutHeader, type MainLayoutHeaderProps } from "./Header";
import { contentWidth, leftBar } from "./layout";
import { MainLayoutNavBar } from "./NavBar";

export interface MainLayoutStructureProps extends Omit<
  MainLayoutHeaderProps,
  "hasRestrictedAccess" | "openSidebar" | "isSiteMapPage"
> {
  customer: CustomerFragment;
  account: AccountFragment;
  children: React.ReactNode;
}

const StyledRoot = styled("div")(({ theme }) => ({
  minHeight: "100vh",

  minWidth: rem(contentWidth),
  [theme.breakpoints.down("lg")]: {
    minWidth: "unset",
    width: "100vw",
  },
}));

const StyledMain = styled("main")(({ theme }) => ({
  display: "flex",
  flex: 1,
  margin: "auto",
  minHeight: `calc(100vh - ${rem(footerHeight)})`,
  padding: `calc(${theme.spacing(4)}) ${theme.spacing(0)}`,

  width: rem(contentWidth),
  [theme.breakpoints.down("lg")]: {
    width: "100vw",
  },

  [theme.breakpoints.between("sm", "lg")]: {
    padding: theme.spacing(1.5),
  },

  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.5),
  },
}));

const StyledLeftBar = styled("div")<DeviceProps>(
  ({ theme, $isSidebarOpen }) => ({
    transition: "left 0.2s ease-in-out",
    backgroundColor: theme.palette.common.white,

    [theme.breakpoints.up("lg")]: {
      width: rem(leftBar),
      minWidth: rem(leftBar),
      marginRight: theme.spacing(4),
    },

    [theme.breakpoints.between("sm", "lg")]: {
      width: "50vw",
    },

    [theme.breakpoints.down("sm")]: {
      width: "100vw",
    },

    [theme.breakpoints.down("lg")]: {
      position: "fixed",
      top: 0,
      left: $isSidebarOpen ? 0 : "-100vw",
      height: "100vh",
      zIndex: 20,
      overflow: $isSidebarOpen ? "auto" : "hidden",
      paddingBottom: theme.spacing(2),
    },
  }),
);

const StyledContent = styled("div")`
  flex: 1;
`;

const Mask = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  backgroundColor: theme.palette.common.black,
  opacity: 0.3,
  zIndex: 10,
}));

export const MainLayoutStructure = ({
  onLogout,
  toProfile,
  toAuth,
  children,
  account,
  customer,
  hasSeveralAccounts,
}: MainLayoutStructureProps) => {
  const { isTabletMobile } = useDeviceWidth();
  const [isOpen, setOpen] = useState(isTabletMobile);
  const isSiteMapPage = useLocation().pathname.includes(SITE_MAP_PAGE);

  const closeSidebar = useCallback(() => {
    setOpen(false);
  }, []);
  const openSidebar = useCallback(() => {
    setOpen(true);
  }, []);
  const hasAccessToGeneralAssembly =
    account.__typename === "CoownerAccount" &&
    account.hasAccessToGeneralAssembly;

  const isDdppBannerFeatureEnabled = useHasFeatureFlagActivated(
    FeatureFlags.CUSTOMER_PORTAL_DDPP_LEGAL_BANNER,
  );
  // The DDPP injunction (ML-25365) targets landlords only — the banner must
  // stay invisible on tenant and co-owner accounts even when the current
  // user is also a landlord at the impacted agency.
  const isLandlordAccount = account.__typename === "LandlordAccount";
  const agencyId = account.branch?.agency.id;
  const ddppBannerVariant =
    agencyId !== undefined
      ? DDPP_BANNER_VARIANT_BY_AGENCY_ID[agencyId]
      : undefined;
  const shouldDisplayDdppBanner =
    isDdppBannerFeatureEnabled &&
    isLandlordAccount &&
    ddppBannerVariant !== undefined;

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isTabletMobile || !isOpen) {
        return;
      }

      if (event.key === "Tab") {
        const focusableElements = document.querySelectorAll(
          'div[role="dialog"] a[href], div[role="dialog"] button, div[role="dialog"] [tabindex]:not([tabindex="-1"])',
        );

        if (focusableElements && focusableElements.length > 0) {
          const firstElement = focusableElements[0] as HTMLElement;
          const lastElement = focusableElements[
            focusableElements.length - 1
          ] as HTMLElement;

          if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          } else if (
            !event.shiftKey &&
            document.activeElement === lastElement
          ) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    },
    [isTabletMobile, isOpen],
  );

  return (
    <StyledRoot>
      <MainLayoutHeader
        hasSeveralAccounts={hasSeveralAccounts}
        isSiteMapPage={isSiteMapPage}
        hasRestrictedAccess={account.hasRestrictedAccess}
        onLogout={onLogout}
        toProfile={toProfile}
        toAuth={toAuth}
        openSidebar={openSidebar}
      />
      {shouldDisplayDdppBanner && ddppBannerVariant !== undefined && (
        <DdppLegalBanner variant={ddppBannerVariant} />
      )}
      <StyledMain role="main" id="main" aria-label={ariaLabel.main}>
        {isTabletMobile && isOpen && <Mask onClick={closeSidebar} />}
        {!isSiteMapPage && (
          <StyledLeftBar
            $isSidebarOpen={isOpen}
            role={isTabletMobile ? "dialog" : undefined}
            aria-label={ariaLabel.menuLabel}
            aria-modal={isTabletMobile ? true : undefined}
            onKeyDown={handleKeyDown}
          >
            {isTabletMobile ? (
              <MainLayoutAccountInfosMobile
                hasSeveralAccounts={hasSeveralAccounts}
                account={account}
                customer={customer}
                toAuth={toAuth}
                closeSidebar={closeSidebar}
                isOpen={isOpen}
              />
            ) : (
              <MainLayoutAccountInfosDesktop
                hasSeveralAccounts={hasSeveralAccounts}
                account={account}
                customer={customer}
                toAuth={toAuth}
              />
            )}

            <MainLayoutNavBar
              hasRestrictedAccess={account.hasRestrictedAccess}
              hasAccessToGeneralAssembly={hasAccessToGeneralAssembly}
              isTrusteeCouncilMember={isTrusteeCouncilMember(account)}
              accountQuality={account.quality}
              closeSidebar={closeSidebar}
              sx={isTabletMobile ? { mb: 8 } : { mt: 4 }}
            />
          </StyledLeftBar>
        )}
        <StyledContent>{children}</StyledContent>
      </StyledMain>
      <Footer />
    </StyledRoot>
  );
};
